import React, { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import Select from "react-select";
import { Switch } from "../../components/ui/switch";

// Helper function to convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};
const productionCategories = [
  "PRODUCCION",
  "LAMINAS",
  "PINTURA",
  "SERVICIOS DE PROVEEDORES",
  "ACCESORIOS",
  "TORNILLERÍA",
];
const perfileriaCategories = ["PERFILERIA"];
const maintenanceCategories = [
  "ALMACEN Y MANTENIMIENTO",
  "MANTENIMIENTO",
  "UTILES PAPELERÍA Y FOTOCOPIAS",
  "DOTACION Y EPPS",
  "BOTIQUIN",
  "MUEBLES Y ENSERES",
  "EQUIPO DE COMPUTO Y COMUNICACION",
  "SERVICIOS DE PROVEEDORES",
  "REPARACIÓN LOCATIVA",
];

// Helper function to add business days
const addBusinessDays = (date, days) => {
  const result = new Date(date);
  let remainingDays = days;

  while (remainingDays > 0) {
    result.setDate(result.getDate() + 1);
    // Check if the day is a weekend (0 = Sunday, 6 = Saturday)
    if (result.getDay() !== 0 && result.getDay() !== 6) {
      remainingDays--;
    }
  }

  return result;
};

const ProductCard = ({
  product,
  productIndex,
  products,
  setProducts,
  field,
  handleInputChange,
  formSubmitted,
  availableItems = [],
  handleFetchSubitems,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [addItemError, setAddItemError] = useState("");

  const handleRemoveProduct = () => {
    const newProducts = products.filter((_, index) => index !== productIndex);
    setProducts(newProducts);
    // Update form values
    const productNames = newProducts.map((p) => p.name);
    handleInputChange(field.id, productNames);
  };

  const handleItemChange = (itemIndex, field, value) => {
    const newProducts = products.map((p, pIndex) => {
      if (pIndex === productIndex) {
        const newItems = p.items.map((item, iIndex) => {
          if (iIndex === itemIndex) {
            return {
              ...item,
              [field]: value,
            };
          }
          return item;
        });
        return { ...p, items: newItems };
      }
      return p;
    });
    setProducts(newProducts);
  };

  const handleKeepChangesToggle = () => {
    const newProducts = products.map((p, idx) =>
      idx === productIndex ? { ...p, keepChanges: !p.keepChanges } : p
    );
    setProducts(newProducts);
  };

  // Add paste handler for items
  const handlePasteItems = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedData = clipboardData.getData("Text");
    const rows = pastedData
      .trim()
      .split("\n")
      .map((row) => row.split("\t"));

    if (rows.length > 0) {
      let newItems = [];

      rows.forEach((row) => {
        if (row.length >= 3) {
          const [name, quantity, unit] = row;
          const existingItem = availableItems.find(
            (item) =>
              item.name?.trim() === name.trim() ||
              item.label?.trim() === name.trim()
          );
          if (existingItem) {
            newItems.push({
              ...existingItem,
              quantity: parseFloat(quantity.trim()) || 0,
              unit: unit.trim(),
            });
          } else {
            newItems.push({
              name: name.trim(),
              label: name.trim(),
              value: name.trim().toLowerCase().replace(/\s+/g, "-"),
              quantity: parseFloat(quantity.trim()) || 0,
              unit: unit.trim(),
            });
          }
        } else if (row.length === 2) {
          const [name, quantity] = row;
          const existingItem = availableItems.find(
            (item) =>
              item.name?.trim() === name.trim() ||
              item.label?.trim() === name.trim()
          );
          if (existingItem) {
            newItems.push({
              ...existingItem,
              quantity: parseFloat(quantity.trim()) || 0,
            });
          } else {
            newItems.push({
              name: name.trim(),
              label: name.trim(),
              value: name.trim().toLowerCase().replace(/\s+/g, "-"),
              quantity: parseFloat(quantity.trim()) || 0,
              unit: "",
            });
          }
        } else if (row.length === 1) {
          const name = row[0].trim();
          const existingItem = availableItems.find(
            (item) =>
              item.name?.trim() === name.trim() ||
              item.label?.trim() === name.trim()
          );
          if (existingItem) {
            newItems.push({
              ...existingItem,
              quantity: 0,
            });
          } else {
            newItems.push({
              name: name,
              label: name,
              value: name.toLowerCase().replace(/\s+/g, "-"),
              quantity: 0,
              unit: "",
            });
          }
        }
      });

      // Add new items to the current product
      const newProducts = products.map((p, pIndex) => {
        if (pIndex === productIndex) {
          const currentItems = p.items || [];
          const itemsWithIds = newItems.map((item, index) => ({
            ...item,
            id: item.id || `temp-${Date.now()}-${index}`,
          }));
          return {
            ...p,
            items: [...currentItems, ...itemsWithIds],
          };
        }
        return p;
      });
      setProducts(newProducts);
    }
  };

  const totalItems = product.items ? product.items.length : 0;

  return (
    <div className="border border-gray-200 rounded shadow-sm bg-white overflow-hidden hover:shadow-md transition-shadow duration-200 self-start">
      {/* Product Header - Always Visible */}
      <div className="px-4 py-3 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
              <svg
                className="w-4 h-4 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 truncate">
                {product.name}
              </h4>
              <div className="flex items-center text-xs text-gray-500 space-x-2">
                <span>ID: {product.id}</span>
                <span>•</span>
                <span>{totalItems} items</span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Switch
                checked={!!product.keepChanges}
                onCheckedChange={handleKeepChangesToggle}
                disabled={formSubmitted}
                id={`keep-changes-switch-${product.id}`}
                className="border border-gray-400 bg-gray-200 data-[state=unchecked]:bg-gray-300"
                style={{
                  border: "1px solid #9ca3af", // Tailwind gray-400
                  backgroundColor: !product.keepChanges ? "#e5e7eb" : undefined, // Tailwind gray-200
                  transition: "background-color 0.2s, border 0.2s",
                }}
              />
              <label
                htmlFor={`keep-changes-switch-${product.id}`}
                className="text-xs text-gray-700 select-none"
              >
                Mantener Cambios
              </label>
            </div>
            {/* Expand/Collapse Button */}

            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              title={isExpanded ? "Contraer" : "Expandir"}
            >
              <svg
                className={`w-4 h-4 transform transition-transform duration-200 ${
                  isExpanded ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Remove Button */}
            <button
              type="button"
              onClick={handleRemoveProduct}
              className="p-1 text-gray-400 hover:text-red-500 transition-colors duration-200"
              title="Eliminar producto"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Collapsible Items Section */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* Product-level Order Quantity Input */}
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-6">
            <div className="flex flex-col items-start">
              <label className="text-xs text-gray-500 mb-1">
                Cantidad a ordenar
              </label>
              <Input
                type="number"
                min="0"
                step="1"
                value={product.quantityToOrder || 0}
                onChange={(e) => {
                  const newProducts = products.map((p, idx) =>
                    idx === productIndex
                      ? {
                          ...p,
                          quantityToOrder: parseFloat(e.target.value) || 0,
                        }
                      : p
                  );
                  setProducts(newProducts);
                }}
                className="w-24 h-8 text-center text-xs border-gray-300 rounded shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0"
                disabled={formSubmitted}
              />
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
            <h5 className="text-xs font-medium text-gray-700 uppercase tracking-wider">
              Items del Producto
            </h5>
          </div>

          {/* Paste Items Textarea */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-700">
                Copiar y pegar items
              </label>
              <Textarea
                onPaste={handlePasteItems}
                placeholder="Copia y pega los items aquí (formato: Nombre [Tab] Cantidad [Tab] Unidad)"
                className="w-full h-20 text-xs border-gray-300 rounded shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                disabled={formSubmitted}
              />
              <p className="text-xs text-gray-500">
                Formato: Nombre [Tab] Cantidad [Tab] Unidad (ej: "Tornillo M4"
                [Tab] "10" [Tab] "UND")
              </p>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            <div className="divide-y divide-gray-100">
              {(product.items || []).map((item, itemIndex) => {
                // Check if item belongs to perfileria category
                const isPerfileriaItem = perfileriaCategories.includes(
                  item.category_name
                );

                return (
                  <div
                    key={`${product.id}-${item.id || itemIndex}-${
                      item.novelty
                    }-${item.mpClassification}`}
                    className="flex flex-col gap-3 px-4 py-3 hover:bg-gray-50 transition-colors duration-150"
                  >
                    {/* Item Info Row */}
                    <div className="flex flex-row items-center justify-between gap-4">
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                          <svg
                            className="w-3 h-3 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                            />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {item.name || item.label}
                          </div>
                          <div className="text-xs text-gray-500">
                            SKU: {item.sku || item.value || "N/A"}
                          </div>
                        </div>
                      </div>

                      {/* Basic Controls */}
                      <div className="flex flex-row items-center gap-4 min-w-[200px] justify-end">
                        {/* Quantity Input */}
                        <div className="flex flex-col items-start">
                          <label className="text-xs text-gray-500 mb-1">
                            Cantidad
                          </label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.quantity || 0}
                            onChange={(e) =>
                              handleItemChange(
                                itemIndex,
                                "quantity",
                                parseFloat(e.target.value) || 0
                              )
                            }
                            className="w-20 h-8 text-center text-xs border-gray-300 rounded shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0"
                            disabled={formSubmitted}
                          />
                        </div>

                        {/* Unidad Dropdown */}
                        <div className="flex flex-col items-start">
                          <label className="text-xs text-gray-500 mb-1">
                            Unidad
                          </label>
                          <Select
                            value={
                              item.unit
                                ? { value: item.unit, label: item.unit }
                                : null
                            }
                            onChange={(selected) =>
                              handleItemChange(
                                itemIndex,
                                "unit",
                                selected ? selected.value : ""
                              )
                            }
                            options={[
                              { value: "UND", label: "UND" },
                              { value: "KG", label: "KG" },
                              { value: "MTS", label: "MTS" },
                              { value: "LT", label: "LT" },
                              { value: "M2", label: "M2" },
                              { value: "M3", label: "M3" },
                              { value: "CAJA", label: "CAJA" },
                              { value: "PAQUETE", label: "PAQUETE" },
                            ]}
                            placeholder="Unidad"
                            classNamePrefix="react-select"
                            className="w-24"
                            menuPortalTarget={
                              typeof window !== "undefined"
                                ? document.body
                                : null
                            }
                            menuPosition="fixed"
                            isDisabled={formSubmitted}
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: "32px",
                                backgroundColor: "white",
                                borderColor: "#d1d5db",
                                borderRadius: "6px",
                                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                                fontSize: "12px",
                                "&:hover": {
                                  borderColor: "#3b82f6",
                                },
                              }),
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              menu: (base) => ({
                                ...base,
                                backgroundColor: "white",
                                borderRadius: "6px",
                                boxShadow:
                                  "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isFocused
                                  ? "#dbeafe"
                                  : "transparent",
                                color: "#374151",
                                fontSize: "12px",
                                padding: "4px 8px",
                                "&:active": {
                                  backgroundColor: "#bfdbfe",
                                },
                              }),
                              singleValue: (base) => ({
                                ...base,
                                fontSize: "12px",
                              }),
                              placeholder: (base) => ({
                                ...base,
                                fontSize: "12px",
                              }),
                            }}
                          />
                        </div>

                        {/* Remove Item Button */}
                        <button
                          type="button"
                          onClick={() => {
                            const newProducts = products.map((p, pIndex) => {
                              if (pIndex === productIndex) {
                                return {
                                  ...p,
                                  items: (p.items || []).filter(
                                    (_, i) => i !== itemIndex
                                  ),
                                };
                              }
                              return p;
                            });
                            setProducts(newProducts);
                          }}
                          className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors duration-200"
                          title="Eliminar item"
                          disabled={formSubmitted}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* File Upload and Subitems Buttons */}
                    <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                      {/* File Upload */}
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          id={`file-input-${product.id}-${item.id || itemIndex}`}
                          className="hidden"
                          multiple
                          onChange={async (e) => {
                            const files = Array.from(e.target.files);
                            const filesWithBase64 = await Promise.all(
                              files.map(async (file) => ({
                                filename: file.name,
                                size_in_bytes: file.size,
                                mime_type: file.type,
                                base64: await fileToBase64(file),
                              }))
                            );

                            const newProducts = products.map((p, pIndex) => {
                              if (pIndex === productIndex) {
                                const newItems = p.items.map((i, iIndex) => {
                                  if (iIndex === itemIndex) {
                                    return {
                                      ...i,
                                      attachments: filesWithBase64,
                                    };
                                  }
                                  return i;
                                });
                                return { ...p, items: newItems };
                              }
                              return p;
                            });
                            setProducts(newProducts);
                          }}
                        />
                        <Button
                          type="button"
                          onClick={() =>
                            document
                              .getElementById(`file-input-${product.id}-${item.id || itemIndex}`)
                              .click()
                          }
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white transition-all duration-200"
                          style={{
                            borderRadius: "4px",
                            border: "none",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                          }}
                          disabled={formSubmitted}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                            />
                          </svg>
                        </Button>
                        {item.attachments?.length > 0 && (
                          <span className="text-xs text-gray-500">
                            ({item.attachments.length})
                          </span>
                        )}
                      </div>

                      {/* Subitems Button */}
                      <Button
                        type="button"
                        onClick={() => {
                          if (handleFetchSubitems && item.id) {
                            handleFetchSubitems(item.id);
                          }
                        }}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-green-500 hover:bg-green-600 text-white transition-all duration-200"
                        style={{
                          borderRadius: "4px",
                          border: "none",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        }}
                        disabled={formSubmitted}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3 w-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>                        
                      </Button>
                    </div>

                    {/* Perfileria-specific fields */}
                    {isPerfileriaItem && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-gray-100">
                        {/* Longitud */}
                        <div className="flex flex-col items-start">
                          <label className="text-xs text-gray-500 mb-1">
                            Longitud
                          </label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.longitude || 0}
                            onChange={(e) =>
                              handleItemChange(
                                itemIndex,
                                "longitude",
                                parseFloat(e.target.value) || 0
                              )
                            }
                            className="w-full h-8 text-center text-xs border-gray-300 rounded shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0"
                            disabled={formSubmitted}
                          />
                        </div>

                        {/* Ángulos/Archivo */}
                        <div className="flex flex-col items-start">
                          <label className="text-xs text-gray-500 mb-1">
                            Ángulos/Archivo
                          </label>
                          <Input
                            type="text"
                            value={item.angles || ""}
                            onChange={(e) =>
                              handleItemChange(
                                itemIndex,
                                "angles",
                                e.target.value
                              )
                            }
                            className="w-full h-8 text-xs border-gray-300 rounded shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Ángulos/Archivo"
                            disabled={formSubmitted}
                          />
                        </div>

                        {/* Novedad */}
                        <div className="flex flex-col items-start">
                          <label className="text-xs text-gray-500 mb-1">
                            Novedad
                          </label>
                          <Select
                            value={
                              item.novelty && item.novelty !== ""
                                ? { value: item.novelty, label: item.novelty }
                                : null
                            }
                            onChange={(selected) => {
                              const noveltyValue = selected
                                ? selected.value
                                : "";
                              const today = new Date();
                              let deliveryDate = new Date(today);

                              // Set delivery date based on novelty type
                              if (noveltyValue === "SOLDADURA") {
                                deliveryDate = addBusinessDays(deliveryDate, 2);
                              } else if (noveltyValue === "MECANIZADO") {
                                deliveryDate = addBusinessDays(deliveryDate, 3);
                              } else if (noveltyValue === "DOBLEZ") {
                                deliveryDate = addBusinessDays(deliveryDate, 2);
                              } else if (noveltyValue === "LASER") {
                                deliveryDate = addBusinessDays(deliveryDate, 1);
                              } else if (
                                noveltyValue === "SERVICIOS EXTERNOS"
                              ) {
                                deliveryDate = addBusinessDays(deliveryDate, 5);
                              }

                              // Update both novelty and delivery date in a single state update
                              const newProducts = products.map((p, pIndex) => {
                                if (pIndex === productIndex) {
                                  const newItems = p.items.map(
                                    (item, iIndex) => {
                                      if (iIndex === itemIndex) {
                                        return {
                                          ...item,
                                          novelty: noveltyValue,
                                          deliveryDate: noveltyValue
                                            ? deliveryDate
                                                .toISOString()
                                                .split("T")[0]
                                            : item.deliveryDate,
                                        };
                                      }
                                      return item;
                                    }
                                  );
                                  return { ...p, items: newItems };
                                }
                                return p;
                              });
                              setProducts(newProducts);
                            }}
                            options={[
                              { value: "SOLDADURA", label: "SOLDADURA" },
                              { value: "MECANIZADO", label: "MECANIZADO" },
                              { value: "DOBLEZ", label: "DOBLEZ" },
                              { value: "LASER", label: "LASER" },
                              {
                                value: "SERVICIOS EXTERNOS",
                                label: "SERVICIOS EXTERNOS",
                              },
                            ]}
                            placeholder="Novedad"
                            classNamePrefix="react-select"
                            className="w-full"
                            menuPortalTarget={
                              typeof window !== "undefined"
                                ? document.body
                                : null
                            }
                            menuPosition="fixed"
                            isDisabled={formSubmitted}
                            isClearable={true}
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: "32px",
                                backgroundColor: "white",
                                borderColor: "#d1d5db",
                                borderRadius: "6px",
                                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                                fontSize: "12px",
                                "&:hover": {
                                  borderColor: "#3b82f6",
                                },
                              }),
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              menu: (base) => ({
                                ...base,
                                backgroundColor: "white",
                                borderRadius: "6px",
                                boxShadow:
                                  "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isFocused
                                  ? "#dbeafe"
                                  : "transparent",
                                color: "#374151",
                                fontSize: "12px",
                                padding: "4px 8px",
                                "&:active": {
                                  backgroundColor: "#bfdbfe",
                                },
                              }),
                              singleValue: (base) => ({
                                ...base,
                                fontSize: "12px",
                              }),
                              placeholder: (base) => ({
                                ...base,
                                fontSize: "12px",
                              }),
                            }}
                          />
                        </div>

                        {/* Fecha de Entrega */}
                        <div className="flex flex-col items-start">
                          <label className="text-xs text-gray-500 mb-1">
                            Fecha de Entrega
                          </label>
                          <Input
                            type="date"
                            value={item.deliveryDate || ""}
                            onChange={(e) =>
                              handleItemChange(
                                itemIndex,
                                "deliveryDate",
                                e.target.value
                              )
                            }
                            className="w-full h-8 text-xs border-gray-300 rounded shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={formSubmitted}
                          />
                        </div>
                      </div>
                    )}

                    {/* Production-specific fields */}
                    {productionCategories.includes(item.category_name) && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2 border-t border-gray-100">
                        {/* Clasificación MP */}
                        <div className="flex flex-col items-start">
                          <label className="text-xs text-gray-500 mb-1">
                            Clasificación MP
                          </label>
                          <Select
                            value={
                              item.mpClassification &&
                              item.mpClassification !== ""
                                ? {
                                    value: item.mpClassification,
                                    label: item.mpClassification,
                                  }
                                : null
                            }
                            onChange={(selected) => {
                              const mpValue = selected ? selected.value : "";
                              const today = new Date();
                              let deliveryDate = new Date(today);

                              // Set delivery date based on MP classification
                              if (mpValue === "TERMINADOS") {
                                deliveryDate = addBusinessDays(deliveryDate, 4);
                              } else if (mpValue === "SOLDADURA") {
                                deliveryDate = addBusinessDays(deliveryDate, 2);
                              }

                              // Update both MP classification and delivery date in a single state update
                              const newProducts = products.map((p, pIndex) => {
                                if (pIndex === productIndex) {
                                  const newItems = p.items.map(
                                    (item, iIndex) => {
                                      if (iIndex === itemIndex) {
                                        return {
                                          ...item,
                                          mpClassification: mpValue,
                                          deliveryDate: mpValue
                                            ? deliveryDate
                                                .toISOString()
                                                .split("T")[0]
                                            : item.deliveryDate,
                                        };
                                      }
                                      return item;
                                    }
                                  );
                                  return { ...p, items: newItems };
                                }
                                return p;
                              });
                              setProducts(newProducts);
                            }}
                            options={[
                              { value: "SOLDADURA", label: "SOLDADURA" },
                              { value: "TERMINADOS", label: "TERMINADOS" },
                            ]}
                            placeholder="Clasificación MP"
                            classNamePrefix="react-select"
                            className="w-full"
                            menuPortalTarget={
                              typeof window !== "undefined"
                                ? document.body
                                : null
                            }
                            menuPosition="fixed"
                            isDisabled={formSubmitted}
                            isClearable={true}
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: "32px",
                                backgroundColor: "white",
                                borderColor: "#d1d5db",
                                borderRadius: "6px",
                                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                                fontSize: "12px",
                                "&:hover": {
                                  borderColor: "#3b82f6",
                                },
                              }),
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              menu: (base) => ({
                                ...base,
                                backgroundColor: "white",
                                borderRadius: "6px",
                                boxShadow:
                                  "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isFocused
                                  ? "#dbeafe"
                                  : "transparent",
                                color: "#374151",
                                fontSize: "12px",
                                padding: "4px 8px",
                                "&:active": {
                                  backgroundColor: "#bfdbfe",
                                },
                              }),
                              singleValue: (base) => ({
                                ...base,
                                fontSize: "12px",
                              }),
                              placeholder: (base) => ({
                                ...base,
                                fontSize: "12px",
                              }),
                            }}
                          />
                        </div>

                        {/* Fecha de Entrega */}
                        <div className="flex flex-col items-start">
                          <label className="text-xs text-gray-500 mb-1">
                            Fecha de Entrega
                          </label>
                          <Input
                            type="date"
                            value={item.deliveryDate || ""}
                            onChange={(e) =>
                              handleItemChange(
                                itemIndex,
                                "deliveryDate",
                                e.target.value
                              )
                            }
                            className="w-full h-8 text-xs border-gray-300 rounded shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={formSubmitted}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Add Item Button */}
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex flex-col md:flex-row md:justify-end md:items-center gap-2">
            <div className="w-full">
              <Select
                value={product.items || []}
                onChange={(items) => {
                  // Update product items to match selected items
                  const newItems = (items || []).map((sel) => {
                    const existing = (product.items || []).find(
                      (i) => i.id === sel.id
                    );
                    return existing ? existing : { ...sel, quantity: 0 };
                  });
                  const newProducts = products.map((p, pIndex) => {
                    if (pIndex === productIndex) {
                      return {
                        ...p,
                        items: newItems,
                      };
                    }
                    return p;
                  });
                  setProducts(newProducts);
                }}
                options={availableItems}
                getOptionLabel={(item) => item.label || item.name}
                getOptionValue={(item) => item.id}
                placeholder="Seleccionar items para el producto"
                isDisabled={formSubmitted}
                classNamePrefix="react-select"
                isMulti
                menuPortalTarget={
                  typeof window !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({ ...base, width: "100%" }),
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {isExpanded && (!product.items || product.items.length === 0) && (
        <div className="px-4 py-6 text-center border-t border-gray-100">
          <div className="text-gray-400 mb-2">
            <svg
              className="w-8 h-8 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <p className="text-gray-500 text-sm">
            Este producto no tiene items asociados
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductCard;
